import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { get as getUser } from 'js/lib/user';

import { isSSR } from 'bundles/common/utils/isSSR';
import SpecializationProgressAndCourses from 'bundles/enterprise-legacy-learner-home/queries/SpecializationProgressAndCourses.graphql';
import {
  type SpecializationProgressAndCoursesQuery,
  type SpecializationProgressAndCoursesQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/SpecializationProgressAndCourses';
import { getNextCourseSlug } from 'bundles/enterprise-legacy-learner-home/utils/courseAndS12nUtils';
import type { GuidedCourseNextStepsV1MultiGetQuery } from 'bundles/naptimejs/resources/__generated__/GuidedCourseNextStepsV1';
import { COURSE_NEXT_STEP_TYPE_NAME } from 'bundles/unified-description-page-common/constants';
import GuidedCourseNextStepsQuery from 'bundles/unified-description-page-common/queries/GuidedProjectNextStepsQuery';
// TODO: Move this to unified-description-page-common
import { getAllSkippableNextStepsExcept } from 'bundles/unified-description-page-common/utils/nextSteps';

export const useSpecializationNextSteps = (s12nId?: string, skip = false) => {
  const userId = getUser()?.id;

  const {
    data,
    loading: loadingS12nNextCourse,
    error,
  } = useQuery<SpecializationProgressAndCoursesQuery, SpecializationProgressAndCoursesQueryVariables>(
    SpecializationProgressAndCourses,
    {
      variables: {
        userId,
        s12nId: s12nId || '',
        s12nIdString: s12nId || '',
      },
      ssr: true,
      skip: !s12nId || !userId || skip,
      context: {
        clientName: 'gatewayGql',
      },
      onError: (errorObj) => {
        if (!isSSR()) {
          Sentry.captureException(errorObj, {
            extra: { message: '[Learner flow optimization] Error calling SpecializationProgressAndCourses for s12n' },
          });
        }
      },
    }
  );

  const s12n = data?.Specialization?.queryById;

  const s12nProgress = data?.OnDemandSpecializationProgress?.progress;
  const nextCourseSlug = s12n ? getNextCourseSlug({ s12n, s12nProgress }) : null;

  const stepsToSkip = getAllSkippableNextStepsExcept(new Set(['CourseMaterialNextStep']));

  const { data: nextStepData, loading: loadingNextStep } = useQuery<GuidedCourseNextStepsV1MultiGetQuery>(
    GuidedCourseNextStepsQuery,
    {
      skip: !s12nId || !userId || !nextCourseSlug || !!error || skip,
      ssr: true,
      variables: {
        ids: `${userId}~${nextCourseSlug}`,
        stepsToSkip: stepsToSkip.join(','),
      },
      onError: (errorObj) => {
        if (!isSSR()) {
          Sentry.captureException(errorObj, {
            extra: { message: '[Learner flow optimization] Error calling GuidedCourseNextStepsV1 for s12n' },
          });
        }
      },
    }
  );

  const courseNextStep = nextStepData?.GuidedCourseNextStepsV1?.multiGet?.elements?.[0];
  const courseNextStepTypeName = courseNextStep?.nextStep?.typeName;
  const resourcePath =
    courseNextStepTypeName === COURSE_NEXT_STEP_TYPE_NAME
      ? courseNextStep?.nextStep.definition.item.resourcePath
      : undefined;
  return {
    resourcePath,
    loading: loadingS12nNextCourse || loadingNextStep,
  };
};
